import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/auth";
import { Link } from "react-router-dom";
import { PageHeader } from "@/pages/Classes";
import { Search, Trophy, Medal, Star } from "lucide-react";

const placementIcon = (title = "") => {
  const t = title.toLowerCase();
  if (t.includes("1st") || t.includes("gold") || t.includes("champion") || t.includes("winner")) return { Icon: Trophy, color: "text-yellow-400" };
  if (t.includes("2nd") || t.includes("silver") || t.includes("runner")) return { Icon: Medal, color: "text-zinc-300" };
  if (t.includes("3rd") || t.includes("bronze")) return { Icon: Medal, color: "text-amber-600" };
  return { Icon: Star, color: "text-red-500" };
};

const CATEGORY_STYLE = {
  team:      "border-red-500/40 text-red-400 bg-red-500/5",
  solo:      "border-sky-500/40 text-sky-400 bg-sky-500/5",
  duo:       "border-fuchsia-500/40 text-fuchsia-400 bg-fuchsia-500/5",
  trio:      "border-violet-500/40 text-violet-400 bg-violet-500/5",
  crew:      "border-orange-500/40 text-orange-400 bg-orange-500/5",
  event:     "border-yellow-500/40 text-yellow-400 bg-yellow-500/5",
  school:    "border-emerald-500/40 text-emerald-400 bg-emerald-500/5",
  community: "border-amber-500/40 text-amber-400 bg-amber-500/5",
};
const categoryPill = (c) => CATEGORY_STYLE[(c || "").toLowerCase()] || "border-zinc-500/40 text-zinc-400 bg-zinc-500/5";

const yearOf = (a) => {
  const raw = (a.date || "").toString();
  const m = raw.match(/\b(19|20)\d{2}\b/);
  return m ? m[0] : (raw || "—");
};

export default function Achievements() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [year, setYear] = useState("");
  const [category, setCategory] = useState("");
  const [crew, setCrew] = useState("");

  useEffect(() => { api.get("/achievements").then(r => setItems(r.data || [])); }, []);

  const years = useMemo(() => {
    const s = new Set(items.map(yearOf).filter(v => v && v !== "—"));
    // Auto-include the current year even if no achievements have been added yet, so admins
    // (and visitors) always see a slot for the year we're in. Future years appear only
    // once an achievement is added for that year.
    const currentYear = String(new Date().getFullYear());
    s.add(currentYear);
    return Array.from(s).sort((a, b) => b.localeCompare(a));
  }, [items]);

  const categories = useMemo(() => {
    const s = new Set(items.map(a => (a.category || "").trim().toLowerCase()).filter(Boolean));
    return Array.from(s).sort();
  }, [items]);

  const crewNames = useMemo(() => Array.from(new Set(items.map(a => (a.crew_name || "").trim()).filter(Boolean))).sort(), [items]);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return items
      .filter(a => !year || yearOf(a) === year)
      .filter(a => !crew || (a.crew_name || "") === crew)
      .filter(a => !category || (a.category || "").toLowerCase() === category)
      .filter(a => !needle || [a.title, a.event, a.description, a.date, a.category, a.crew_name]
        .filter(Boolean).some(v => String(v).toLowerCase().includes(needle)))
      .sort((a, b) => {
        const ya = yearOf(a), yb = yearOf(b);
        if (ya !== yb) return yb.localeCompare(ya);
        return (a.order ?? 0) - (b.order ?? 0);
      });
  }, [items, q, year, category, crew]);

  return (
    <>
      <PageHeader eyebrow="Podiums & stages" title="Accolades" sub={`${items.length} recorded milestones — competitions, showcases, community accolades. Filter by year or category, or search by title / event.`} />
      <div className="max-w-[1300px] mx-auto px-4 sm:px-6 py-10">

        {/* Toolbar */}
        <div className="flex flex-wrap items-center gap-2 mb-3" data-testid="achievements-toolbar">
          <div className="relative flex-1 min-w-[240px] max-w-md">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500"/>
            <input
              value={q}
              onChange={e => setQ(e.target.value)}
              placeholder="Search accolades, events, notes…"
              className="w-full bg-black border border-white/10 pl-9 pr-3 py-2 text-sm focus:border-red-600 outline-none"
              data-testid="achievements-search"
            />
          </div>
          <div className="flex gap-1 flex-wrap" data-testid="year-filters">
            <button
              onClick={() => setYear("")}
              className={`px-3 py-2 border text-xs uppercase tracking-widest ${!year ? "border-red-600 bg-red-600/10 text-white" : "border-white/10 text-zinc-400 hover:border-white/30"}`}
              data-testid="year-all"
            >All years</button>
            {years.map(y => (
              <button
                key={y}
                onClick={() => setYear(y === year ? "" : y)}
                className={`px-3 py-2 border text-xs uppercase tracking-widest ${year === y ? "border-red-600 bg-red-600/10 text-white" : "border-white/10 text-zinc-400 hover:border-white/30"}`}
                data-testid={`year-${y}`}
              >{y}</button>
            ))}
          </div>
        </div>

        {crewNames.length > 0 && (
          <div className="flex gap-1 flex-wrap mb-3" data-testid="crew-filters">
            <span className="text-[10px] uppercase tracking-widest text-zinc-500 self-center mr-1">Crew</span>
            <button onClick={() => setCrew("")} className={`px-3 py-1.5 border text-[11px] uppercase tracking-widest ${!crew ? "border-red-600 bg-red-600/10 text-white" : "border-white/10 text-zinc-400 hover:border-white/30"}`} data-testid="crew-all">All</button>
            {crewNames.map(n => (
              <button key={n} onClick={() => setCrew(n === crew ? "" : n)} className={`px-3 py-1.5 border text-[11px] uppercase tracking-widest ${crew === n ? "border-red-600 bg-red-600/10 text-white" : "border-white/10 text-zinc-400 hover:border-white/30"}`} data-testid={`crew-${n.replace(/\s+/g, "-").toLowerCase()}`}>{n}</button>
            ))}
          </div>
        )}
        {categories.length > 0 && (
          <div className="flex gap-1 flex-wrap mb-6" data-testid="category-filters">
            <span className="text-[10px] uppercase tracking-widest text-zinc-500 self-center mr-1">Category</span>
            <button
              onClick={() => setCategory("")}
              className={`px-3 py-1.5 border text-[11px] uppercase tracking-widest ${!category ? "border-red-600 bg-red-600/10 text-white" : "border-white/10 text-zinc-400 hover:border-white/30"}`}
              data-testid="cat-all"
            >All</button>
            {categories.map(c => (
              <button
                key={c}
                onClick={() => setCategory(c === category ? "" : c)}
                className={`px-3 py-1.5 border text-[11px] uppercase tracking-widest ${category === c ? "border-red-600 bg-red-600/10 text-white" : "border-white/10 text-zinc-400 hover:border-white/30"}`}
                data-testid={`cat-${c}`}
              >{c}</button>
            ))}
          </div>
        )}

        {/* Table */}
        <div className="card-surface overflow-x-auto" data-testid="achievements-table">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-white/10 text-zinc-500 uppercase tracking-widest text-[10px]">
                <th className="text-left px-4 py-3 w-16"></th>
                <th className="text-left px-4 py-3 w-24">Year</th>
                <th className="text-left px-4 py-3 w-28">Category</th>
                <th className="text-left px-4 py-3">Placement</th>
                <th className="text-left px-4 py-3">Event / Competition</th>
                <th className="text-left px-4 py-3 w-40">Crew</th>
                <th className="text-left px-4 py-3">Notes</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((a) => {
                const { Icon, color } = placementIcon(a.title);
                const cat = (a.category || "").toLowerCase();
                return (
                  <tr key={a.id} className="border-b border-white/5 hover:bg-white/[0.02]" data-testid={`ach-row-${a.id}`}>
                    <td className="px-4 py-3">
                      <Icon size={18} className={color} />
                    </td>
                    <td className="px-4 py-3 text-zinc-300 tabular-nums">{yearOf(a)}</td>
                    <td className="px-4 py-3">
                      {cat ? (
                        <span className={`inline-block text-[10px] uppercase tracking-widest px-2 py-1 border ${categoryPill(cat)}`}>{cat}</span>
                      ) : <span className="text-zinc-600">—</span>}
                    </td>
                    <td className="px-4 py-3 font-display text-lg text-white">{a.title || "—"}</td>
                    <td className="px-4 py-3 text-zinc-200">{a.event || "—"}</td>
                    <td className="px-4 py-3" data-testid={`ach-crew-${a.id}`}>{a.crew_name ? <Link to="/competition-team" className="inline-block px-2 py-0.5 border border-orange-500/40 text-orange-400 bg-orange-500/5 text-[10px] uppercase tracking-widest hover:border-orange-400">{a.crew_name}</Link> : <span className="text-zinc-600">—</span>}</td>
                    <td className="px-4 py-3 text-zinc-400 text-xs">{a.description || ""}</td>
                  </tr>
                );
              })}
              {filtered.length === 0 && (
                <tr><td colSpan={7} className="text-center py-20 text-zinc-500">
                  {items.length === 0 ? "No accolades yet." : "No accolades match this filter."}
                </td></tr>
              )}
            </tbody>
          </table>
        </div>

        {items.length > 0 && (
          <div className="text-zinc-500 text-xs mt-4" data-testid="achievements-count">
            Showing <strong className="text-white">{filtered.length}</strong> of {items.length} accolades.
          </div>
        )}
      </div>
    </>
  );
}
